import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Course } from '../../types';
import { fetchCourses } from '../../services/api';
import {
  CourseFacet,
  courseMatchesFacet,
  courseMatchesSearch,
  getCourseFacetValues,
  normalizeCourseMetadata,
} from '../../utils/courseMetadata';
import MarkdownContent from '../shared/MarkdownContent';

interface CourseSelectorProps {
  disabled?: boolean;
}

/** Preferred display order for levels; anything unknown goes after, alphabetically. */
const LEVEL_ORDER = ['Eleven Plus', 'GCSE / High School', 'A Level', 'University', 'Professional Development', 'General'];

const TAGS_COLLAPSED_COUNT = 8;

const readMultiParam = (params: URLSearchParams, key: string): string[] =>
  params
    .getAll(key)
    .reduce<string[]>((values, value) => values.concat(value.split(',')), [])
    .map(value => value.trim())
    .filter(Boolean);

const CourseSelector: React.FC<CourseSelectorProps> = ({ disabled }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedSubjects = useMemo(() => readMultiParam(searchParams, 'subject'), [searchParams]);
  const selectedLevels = useMemo(() => readMultiParam(searchParams, 'level'), [searchParams]);
  const selectedTags = useMemo(() => readMultiParam(searchParams, 'tags'), [searchParams]);

  const selectedByFacet: Record<CourseFacet, string[]> = useMemo(() => ({
    subject: selectedSubjects,
    level: selectedLevels,
    tags: selectedTags,
  }), [selectedSubjects, selectedLevels, selectedTags]);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        const coursesData = await fetchCourses();
        setCourses(coursesData);
      } catch (err) {
        setError('Failed to load courses');
        console.error('Error fetching courses:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  const toggleFilter = useCallback((facet: CourseFacet, value: string) => {
    const next = new URLSearchParams(searchParams);
    const current = readMultiParam(next, facet);
    const normalized = normalizeCourseMetadata(value);
    const updated = current.some(item => normalizeCourseMetadata(item) === normalized)
      ? current.filter(item => normalizeCourseMetadata(item) !== normalized)
      : [...current, value];
    next.delete(facet);
    updated.forEach(item => next.append(facet, item));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('subject');
    next.delete('level');
    next.delete('tags');
    setSearchParams(next, { replace: true });
    setSearchQuery('');
  }, [searchParams, setSearchParams]);

  const matchesSearch = useCallback((course: Course) => {
    return courseMatchesSearch(course, searchQuery);
  }, [searchQuery]);

  const matchesFacet = useCallback((course: Course, facet: CourseFacet) => {
    return courseMatchesFacet(course, facet, selectedByFacet[facet]);
  }, [selectedByFacet]);

  const filteredCourses = useMemo(() => {
    const result = courses.filter(course =>
      matchesSearch(course) &&
      matchesFacet(course, 'subject') &&
      matchesFacet(course, 'level') &&
      matchesFacet(course, 'tags')
    );
    return result.sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [courses, matchesSearch, matchesFacet]);

  /**
   * Facet options with counts. Counts reflect the courses that would match if
   * this option were the only selection in its own facet (standard faceted
   * filtering: options within a facet are OR'd, facets are AND'ed).
   */
  const facetOptions = useMemo(() => {
    const build = (facet: CourseFacet) => {
      const otherFacets = (['subject', 'level', 'tags'] as CourseFacet[]).filter(key => key !== facet);
      const candidates = courses.filter(course =>
        matchesSearch(course) && otherFacets.every(key => matchesFacet(course, key))
      );
      const counts = new Map<string, { value: string; count: number }>();
      // Register every value in the catalogue so options don't vanish while filtering
      for (const course of courses) {
        const values = getCourseFacetValues(course, facet);
        for (const value of values) {
          if (!value.trim()) continue;
          const key = normalizeCourseMetadata(value);
          if (!counts.has(key)) counts.set(key, { value: value.trim(), count: 0 });
        }
      }
      for (const course of candidates) {
        const values = getCourseFacetValues(course, facet);
        for (const value of values) {
          const entry = counts.get(normalizeCourseMetadata(value));
          if (entry) entry.count += 1;
        }
      }
      return Array.from(counts.values());
    };

    const subjects = build('subject').sort((a, b) => a.value.localeCompare(b.value));
    const levels = build('level').sort((a, b) => {
      const aIndex = LEVEL_ORDER.indexOf(a.value);
      const bIndex = LEVEL_ORDER.indexOf(b.value);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.value.localeCompare(b.value);
    });
    // Most common tags first so the collapsed list shows the most useful ones
    const tags = build('tags').sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    return { subjects, levels, tags };
  }, [courses, matchesSearch, matchesFacet]);

  const activeFilters = useMemo(() => ([
    ...selectedSubjects.map(value => ({ facet: 'subject' as CourseFacet, value })),
    ...selectedLevels.map(value => ({ facet: 'level' as CourseFacet, value })),
    ...selectedTags.map(value => ({ facet: 'tags' as CourseFacet, value })),
  ]), [selectedSubjects, selectedLevels, selectedTags]);

  const hasActiveFilters = activeFilters.length > 0 || searchQuery.trim().length > 0;

  const isSelected = (facet: CourseFacet, value: string) =>
    selectedByFacet[facet].some(
      item => normalizeCourseMetadata(item) === normalizeCourseMetadata(value)
    );

  const renderFilterGroup = (
    title: string,
    facet: CourseFacet,
    options: { value: string; count: number }[],
    collapsible = false
  ) => {
    if (options.length === 0) return null;
    const visibleOptions = collapsible && !tagsExpanded
      ? options.slice(0, TAGS_COLLAPSED_COUNT)
      : options;
    const hiddenCount = options.length - visibleOptions.length;
    return (
      <fieldset className="browse-filter-group">
        <legend>{title}</legend>
        <div className="browse-filter-options">
          {visibleOptions.map(option => (
            <label
              key={option.value}
              className={`browse-filter-option${option.count === 0 && !isSelected(facet, option.value) ? ' is-empty' : ''}`}
            >
              <input
                type="checkbox"
                checked={isSelected(facet, option.value)}
                onChange={() => toggleFilter(facet, option.value)}
              />
              <span className="browse-filter-option-label">{option.value}</span>
              <span className="browse-filter-option-count">{option.count}</span>
            </label>
          ))}
        </div>
        {collapsible && options.length > TAGS_COLLAPSED_COUNT && (
          <button
            type="button"
            className="browse-filter-more"
            onClick={() => setTagsExpanded(expanded => !expanded)}
          >
            {tagsExpanded ? 'Show fewer' : `Show all (${hiddenCount} more)`}
          </button>
        )}
      </fieldset>
    );
  };

  const renderCourseCard = (course: Course) => (
    <Link
      key={course.id}
      to={disabled ? '#' : `/course/${course.id}`}
      className={`browse-course-card ${disabled ? 'disabled' : ''}`}
      onClick={(e) => disabled && e.preventDefault()}
    >
      <div className="browse-course-card-top">
        <div className="course-meta">
          {course.subjects.map(subject => (
            <span className="course-subject" key={subject}>{subject}</span>
          ))}
          {course.levels.map(level => (
            <span className="course-level" key={level}>{level}</span>
          ))}
        </div>
      </div>
      <div className="browse-course-card-body">
        <h3>{course.title}</h3>
        <div className="browse-course-description course-markdown course-markdown--compact">
          <MarkdownContent content={course.description || course.detailed_description || 'Explore this course.'} />
        </div>
      </div>
      <div className="browse-course-card-footer">
        <span>{course.lesson_count || 0} lesson{(course.lesson_count || 0) === 1 ? '' : 's'}</span>
        <span className="browse-course-arrow">View course <span aria-hidden="true">→</span></span>
      </div>
    </Link>
  );

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="browse-layout">
      <button
        type="button"
        className="browse-filters-toggle"
        onClick={() => setFiltersOpen(open => !open)}
        aria-expanded={filtersOpen}
      >
        {filtersOpen ? 'Hide filters' : 'Filters'}
        {activeFilters.length > 0 && <span className="browse-filters-toggle-badge">{activeFilters.length}</span>}
      </button>

      <aside className={`browse-sidebar${filtersOpen ? ' is-open' : ''}`} aria-label="Course filters">
        <div className="course-search-container">
          <input
            type="text"
            className="course-search-input"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search courses"
          />
          {searchQuery && (
            <button
              className="course-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <button type="button" className="browse-clear-filters" onClick={clearAllFilters}>
            Clear all filters
          </button>
        )}

        {renderFilterGroup('Level', 'level', facetOptions.levels)}
        {renderFilterGroup('Subject', 'subject', facetOptions.subjects)}
        {renderFilterGroup('Tags', 'tags', facetOptions.tags, true)}
      </aside>

      <section className="browse-content" aria-label="Course results">
        <div className="browse-results-bar">
          <h1>Browse courses</h1>
          <span className="browse-results-count">
            {filteredCourses.length} course{filteredCourses.length === 1 ? '' : 's'}
          </span>
        </div>

        {activeFilters.length > 0 && (
          <div className="browse-active-filters" aria-label="Active filters">
            {activeFilters.map(filter => (
              <button
                type="button"
                key={`${filter.facet}:${filter.value}`}
                className="browse-active-filter-chip"
                onClick={() => toggleFilter(filter.facet, filter.value)}
              >
                {filter.value}
                <span aria-hidden="true">×</span>
              </button>
            ))}
            <button type="button" className="browse-active-filters-clear" onClick={clearAllFilters}>
              Clear all
            </button>
          </div>
        )}

        {courses.length === 0 ? (
          <div className="empty-state">
            <p>No courses available yet.</p>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="empty-state">
            <p>No courses match your filters.</p>
            <button type="button" className="browse-clear-filters" onClick={clearAllFilters}>
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="browse-course-grid">
            {filteredCourses.map(renderCourseCard)}
          </div>
        )}
      </section>
    </div>
  );
};

export default CourseSelector;
